/**
 * Credenciales de acceso del portal de transparencia de un cliente.
 * Solo owner/admin. Con contraseña configurada el portal exige login;
 * quitarla vuelve el portal abierto (acceso solo por enlace).
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useMe } from "@/hooks/use-me";
import { media, type AgencyClientOut } from "@/lib/api";

export function PortalCredentialsForm({ client }: { client: AgencyClientOut }) {
  const qc = useQueryClient();
  const { data: me } = useMe();
  const canManage = me?.role === "owner" || me?.role === "admin" || me?.role === "superadmin";
  const [username, setUsername] = useState(client.portal_access_user ?? "");
  const [password, setPassword] = useState("");

  const save = useMutation({
    mutationFn: (body: { username?: string | null; password?: string | null }) =>
      media.setClientPortalCredentials(client.id, body),
    onSuccess: (_data, body) => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      setPassword("");
      toast.success(body.password
        ? "Credenciales del portal guardadas"
        : "Login del portal desactivado — acceso abierto por enlace");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!canManage) return null;

  function submit() {
    if (password.length < 8) { toast.error("Contraseña mínimo 8 caracteres"); return; }
    save.mutate({ username: username.trim() || null, password });
  }

  return (
    <div className="pt-3 border-t border-ink-100 dark:border-ink-800 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <KeyRound size={12} /> Credenciales de acceso
        </Label>
        {client.portal_login_required ? (
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
            <Lock size={10} className="mr-1" /> Login requerido
          </Badge>
        ) : (
          <Badge variant="outline">
            <Unlock size={10} className="mr-1" /> Acceso abierto
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-ink-500">
        {client.portal_login_required
          ? <>El portal pide login{client.portal_access_user && <> (usuario <strong>{client.portal_access_user}</strong>)</>}. Guardar una contraseña nueva reemplaza la anterior e invalida las sesiones activas.</>
          : "Configura usuario y contraseña para que el portal exija login a los visitantes."}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Usuario (opcional)</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="auditor" autoComplete="off" />
        </div>
        <div>
          <Label className="text-[11px]">Contraseña nueva</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Mín. 8, letras y números" autoComplete="new-password" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" onClick={submit}
          disabled={save.isPending || !password}>
          <Lock size={13} /> {client.portal_login_required ? "Actualizar credenciales" : "Activar login"}
        </Button>
        {client.portal_login_required && (
          <Button size="sm" variant="outline" disabled={save.isPending}
            onClick={() => save.mutate({ password: null })}>
            <Unlock size={13} /> Quitar login
          </Button>
        )}
      </div>
    </div>
  );
}
